import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxIcon, FmlxTooltip, FmlxTextBox} from 'fmlx-common-ui';
import ToggleRadioButton from '../../components/ToggleRadioButton';
import {
  WellBottomProfile,
  WellSurfaceDataShape,
  WellCount,
  PlateType,
  DefaultWellValue,
} from '../../constants/dataEnum';
import {checkContainIllegalChars} from '../../lib/stringHelper';
import withIntl from '../../lib/withIntl';
import {
  MicroplateWellList,
  DefaultPlateConfiguration,
  WellLabel,
  OtherPlatesList,
  IFCWellList,
} from './PlateTypeEditorEnum';
import {useClickOutside} from '../../lib/customHooks';
import {findKeyByValue} from '../../lib/objectHelper';
import {getMicroplateLabel, getPlateName, getWellSelectionLabel} from './plateTypeHelper';

function ConycalWellBottom({className}) {
  return (
    <svg
      className={className}
      width="20"
      height="25"
      viewBox="0 0 23 25"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 0.0473633H24V12.0474C24 14.1748 16.3774 24.0474 12 24.0474C7.62258 24.0474 0 14.1748 0 12.0474V0.0473633Z"
        fill="currentColor"
      />
    </svg>
  );
}

ConycalWellBottom.propTypes = {
  className: PropTypes.string.isRequired,
};

function PlateDescriptionField({label, placeholder, onChange, onBlur, value, mode, isRequired, isError}) {
  const inputRef = useRef();
  const onEnterHandler = (newValue) => {
    inputRef.current.blur();
    onBlur(newValue);
  };
  return (
    <span className="plate-description__field">
      <p className="label-plate-info">
        {label}
        {isRequired && <sup style={{color: 'red'}}>*</sup>}
      </p>
      <FmlxTextBox
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        required={isError}
        value={value}
        mode={mode}
        onChange={({value: newValue}) => onChange(newValue)}
        onBlur={({value: newValue}) => onBlur(newValue)}
        onEnterKeyPressed={({value: newValue}) => onEnterHandler(newValue)}
      />
      {isError && <span className="warning">The name is already taken, please specify another name.</span>}
    </span>
  );
}

PlateDescriptionField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  isRequired: PropTypes.bool,
  isError: PropTypes.bool,
  mode: PropTypes.string,
};

PlateDescriptionField.defaultProps = {
  onBlur: () => {},
  onChange: () => {},
  isRequired: false,
  isError: false,
  mode: 'text',
};

function PlateViewInfoDesc({
  plateTypeEditorState,
  onSetWellBottomProfile,
  onSetWellShape,
  onSetWellNumber,
  onSetWellLabel,
  onSetPlateName,
  onSetPlateType,
  onSetColPitchAssaySample,
  onSetPartNumber,
  onSetVendor,
  onSetNotes,
  intl,
  isEdit,
  maxCharacters,
  labwareBusinessIsLabwareNameAlreadyExist,
  onSetColumnPitch,
  onSetRowPitch,
}) {
  const {processedStep} = plateTypeEditorState;
  const [showIFCOptions, setShowIFCOptions] = useState(false);
  const [wellNumberOptions, setWellNumberOptions] = useState([]);

  const IFCOptionsRef = useRef(null);
  const selectedIFC = useRef(null);

  function handleChangeWellBottom(value) {
    onSetWellBottomProfile(value);
  }

  function handleChangeWellShape(value) {
    const {wellBottomProfile} = plateTypeEditorState;
    onSetWellShape(value);

    if (value === WellSurfaceDataShape.RECTANGLE) {
      if (wellBottomProfile === WellBottomProfile.ROUNDED) {
        onSetWellBottomProfile(WellBottomProfile.FLAT);
      }
      // disable WellBottomProfile.ROUNDED
    } else {
      // un-disable WellBottomProfile.ROUNDED
    }
  }
  function handleChangeWellNumberIFC(label, wellCount, isValueIFCOptions) {
    if (isValueIFCOptions) {
      // user clicks one of the IFC options
      const {wellBottomProfile, wellShape} = DefaultPlateConfiguration[label];

      onSetWellNumber(wellCount);
      onSetWellLabel(label);
      onSetWellBottomProfile(wellBottomProfile);
      onSetWellShape(wellShape);
      onSetPlateType(PlateType.IFC);

      selectedIFC.current = label;

      if (label === WellLabel.WELL_IFC_192_24) {
        onSetColPitchAssaySample(DefaultWellValue.WELL_IFC_192_24.columnPitchAssaySample);
      }

      setShowIFCOptions(false);
    } else {
      // user clicks the IFC dropdown button
      setShowIFCOptions(!showIFCOptions);
    }
  }
  function handleChangeWellNumberSpearBio(label, wellCount, isSpearBio) {
    if (isSpearBio) {
      const {wellBottomProfile, wellShape, columnPitch, rowPitch} = DefaultPlateConfiguration[label];

      onSetWellNumber(wellCount);
      onSetWellLabel(label);
      onSetWellBottomProfile(wellBottomProfile);
      onSetWellShape(wellShape);
      onSetPlateType(PlateType.SPEAR_BIO);
      onSetColumnPitch(columnPitch);
      onSetRowPitch(rowPitch);

      selectedIFC.current = null;

      setShowIFCOptions(false);
    } else {
      // user clicks the IFC dropdown button
      setShowIFCOptions(!showIFCOptions);
    }
  }

  function handleChangeWellNumber(label, id, optionValue) {
    const {wellBottomProfile, wellShape} = plateTypeEditorState;

    const wellKey = findKeyByValue(WellLabel, (el) => el === label);
    const wellCount = WellCount[wellKey];
    const isIFCPlate = IFCWellList.includes(optionValue);
    const isSpearBioPlate = optionValue === WellLabel.WELL_SPEAR_BIO;

    // IFC options
    if (id >= MicroplateWellList.length || isIFCPlate) {
      handleChangeWellNumberIFC(label, wellCount, isIFCPlate);
      return;
    }

    if (isSpearBioPlate) {
      handleChangeWellNumberSpearBio(label, wellCount, isSpearBioPlate);
      return;
    }

    // Microplate options
    onSetPlateType(PlateType.MICROPLATE);
    onSetWellNumber(wellCount);
    onSetWellLabel(label);

    setShowIFCOptions(false);

    selectedIFC.current = null;

    if (wellCount === WellCount.WELL_1) {
      if (wellBottomProfile === WellBottomProfile.CONYCAL || wellBottomProfile === WellBottomProfile.ROUNDED) {
        onSetWellBottomProfile(WellBottomProfile.FLAT);
      }

      if (wellShape === WellSurfaceDataShape.CIRCLE) {
        onSetWellShape(WellSurfaceDataShape.RECTANGLE);
      }

      // disable WellBottomProfile.ROUNDED
      // disable Shape.CONYCAL
      // disable Shape.ROUNDED
    } else if (wellCount === WellCount.WELL_8 || wellCount === WellCount.WELL_12) {
      if (wellShape === WellSurfaceDataShape.CIRCLE) {
        onSetWellShape(WellSurfaceDataShape.RECTANGLE);
      }

      if (wellBottomProfile === WellBottomProfile.ROUNDED) {
        onSetWellBottomProfile(WellBottomProfile.FLAT);
      }
      // un-disable WellBottomProfile.ROUNDED
      // un-disable Shape.CONYCAL
      // un-disable Shape.ROUNDED if WellSurfaceDataShape.CIRCLE, disable if WellSurfaceDataShape.RECTANGLE
    }
  }

  function handleChangeInputPlateName(value) {
    if (!checkContainIllegalChars(value) && value.length <= maxCharacters) {
      onSetPlateName(value);
    }
  }

  function handleBlurInputPlateName() {
    const {plateName} = plateTypeEditorState;
    const newValue = plateName.trim();

    handleChangeInputPlateName(newValue);
  }

  function handleBlurInputPartNumber(value) {
    onSetPartNumber(value);
  }

  function handleBlurInputVendor(value) {
    onSetVendor(value);
  }

  function handleBlurInputNotes(value) {
    onSetNotes(value);
  }

  const {
    currentWizardView,
    wellNumber,
    wellLabel,
    wellBottomProfile,
    wellShape,
    plateType,
    plateName,
    partNumber,
    vendor,
    notes,
    id,
  } = plateTypeEditorState;

  const wellShapeImage = `B_Step1_TopView${wellShape}`;
  const wellNumberImage = useMemo(() => {
    if (plateType === PlateType.IFC) {
      return `A_Step1_${wellLabel.replace('.', '_') || WellCount.WELL_1}${wellShape}`;
    }
    if (plateType === PlateType.SPEAR_BIO) {
      return `A_Step1_${plateType}${wellLabel.replace('.', '_')}`;
    }
    return `A_Step1_${wellNumber || WellCount.WELL_1}${wellShape}`;
  }, [plateType, wellNumber, wellLabel, wellShape]);

  let wellBottomImage = '';

  if (wellBottomProfile === WellBottomProfile.FLAT) {
    wellBottomImage = 'B_Step1_SideViewFlat';
  } else if (wellBottomProfile === WellBottomProfile.CONYCAL) {
    wellBottomImage = 'B_Step1_SideViewConycal';
  } else {
    wellBottomImage = 'B_Step1_SideViewRounded';
  }

  const isNameAlreadyTaken =
    plateName && plateName.trim().length > 0
      ? labwareBusinessIsLabwareNameAlreadyExist(plateName, isEdit ? id : null)
      : false;

  function getWellNumberOptions() {
    const options = [];

    // add microplate options
    MicroplateWellList.forEach((el, idx) => {
      options.push({
        id: idx,
        text: <div style={{height: '25px', paddingTop: '5px'}}>{getMicroplateLabel(el)}</div>,
        disabled: (wellNumber !== el && processedStep > 0) || isEdit,
        value: el,
      });
    });

    // add OtherPlatesList options
    options.push({
      id: MicroplateWellList.length,
      value: [PlateType.IFC, PlateType.SPEAR_BIO, PlateType.MICROPLATE].includes(plateType) && wellLabel,
      text: (
        <div className="plate-view-info-desc__well-property-selection-custom" ref={IFCOptionsRef}>
          <div className="selection-display">
            <span>{getWellSelectionLabel(wellLabel)}</span>
            <span className={`glyphicon glyphicon-triangle-${showIFCOptions ? 'top' : 'bottom'}`} />
          </div>
          {showIFCOptions && (
            <div className="dropdown-container">
              {OtherPlatesList.map((element, idx) => (
                <div
                  role="button"
                  className="dropdown-item"
                  tabIndex={0}
                  onClick={() => handleChangeWellNumber(element, idx, element)}
                  onKeyDown={() => handleChangeWellNumber(element, idx, element)}
                >
                  {getPlateName(element)}
                </div>
              ))}
            </div>
          )}
        </div>
      ),
      disabled: (wellNumber !== WellCount.WELL_IFC && processedStep > 0) || isEdit,
    });

    setWellNumberOptions(options);
  }

  useEffect(() => {
    if (currentWizardView === 1) getWellNumberOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIFCOptions, currentWizardView, processedStep, plateType]);

  useClickOutside(IFCOptionsRef, () => {
    if (showIFCOptions) {
      setShowIFCOptions(false);
    }
  });

  return (
    <div className="plate-view-info-desc override-bootstrap-iso">
      <div className="plate-view-info-desc__plate-general-information">
        <div className="plate-general-information__title">
          <div className="section-title bordered">
            <span>General Information</span>
            &nbsp;
            <FmlxTooltip
              title={intl.formatMessage({
                id: 'plateTypeEditorContainer.generalInformation.tooltip',
              })}
              placement="bottom"
            >
              <span>
                <FmlxIcon name="InfoOutline" size="xs" />
              </span>
            </FmlxTooltip>
          </div>
          <span className="section-additional-info italic">Specify the plate information below.</span>
        </div>
        <span className="plate-general-information__section-container">
          <div className="plate-general-information__left-section">
            <div className="plate-view-info-desc__well-property-selection">
              <p className="label-well-selection">
                Select Well Bottom Profile
                <sup style={{color: 'red'}}>*</sup>
              </p>
              <div className="plate-view-info-desc__well-property-options well-bottom-container">
                <ToggleRadioButton
                  options={[
                    {
                      id: 0,
                      value: WellBottomProfile.FLAT,
                      text: <div className="well-bottom-flat img-by-css" alt="Flat" />,
                      disabled:
                        (wellBottomProfile !== WellBottomProfile.FLAT && processedStep > 0) ||
                        plateType === PlateType.IFC ||
                        plateType === PlateType.SPEAR_BIO ||
                        isEdit,
                    },
                    {
                      id: 1,
                      value: WellBottomProfile.CONYCAL,
                      text: (
                        <span style={{width: '20px', height: '20px'}}>
                          <ConycalWellBottom className="well-bottom-conycal img-by-svg" />
                        </span>
                      ),
                      disabled:
                        wellNumber === WellCount.WELL_1 ||
                        (wellBottomProfile !== WellBottomProfile.CONYCAL && processedStep > 0) ||
                        plateType === PlateType.SPEAR_BIO ||
                        isEdit,
                    },
                    {
                      id: 2,
                      value: WellBottomProfile.ROUNDED,
                      text: <div className="well-bottom-rounded img-by-css" alt="Rounded" />,
                      disabled:
                        wellNumber === WellCount.WELL_1 ||
                        wellNumber === WellCount.WELL_8 ||
                        wellNumber === WellCount.WELL_12 ||
                        wellShape === WellSurfaceDataShape.RECTANGLE ||
                        plateType === PlateType.IFC ||
                        (wellBottomProfile !== WellBottomProfile.ROUNDED && processedStep > 0) ||
                        plateType === PlateType.SPEAR_BIO ||
                        isEdit,
                    },
                  ]}
                  selectedValue={wellBottomProfile}
                  allowDisable
                  onChange={(value) => {
                    handleChangeWellBottom(value);
                  }}
                />
                <img
                  className="image-well-preview well-bottom-preview"
                  alt="Well Bottom Profile"
                  src={`./svg/plate-type-icon/${wellBottomImage}.svg`}
                />
              </div>
            </div>
            <div className="plate-view-info-desc__well-property-selection">
              <p className="label-well-selection">
                Select Well Shape
                <sup style={{color: 'red'}}>*</sup>
              </p>
              <div className="plate-view-info-desc__well-property-options well-surface-container">
                <ToggleRadioButton
                  options={[
                    {
                      id: 0,
                      value: WellSurfaceDataShape.CIRCLE,
                      text: <div className="well-surface-circle img-by-css" />,
                      disabled:
                        wellLabel === WellLabel.WELL_1 ||
                        wellLabel === WellLabel.WELL_8 ||
                        wellLabel === WellLabel.WELL_12 ||
                        wellLabel === WellLabel.WELL_IFC_96_96 ||
                        (wellShape !== WellSurfaceDataShape.CIRCLE && processedStep > 0) ||
                        plateType === PlateType.SPEAR_BIO ||
                        isEdit,
                    },
                    {
                      id: 1,
                      value: WellSurfaceDataShape.RECTANGLE,
                      text: <div className="well-bottom-flat img-by-css" />,
                      disabled:
                        wellLabel === WellLabel.WELL_IFC_192_24 ||
                        wellLabel === WellLabel.WELL_IFC_48_48 ||
                        (wellShape !== WellSurfaceDataShape.RECTANGLE && processedStep > 0) ||
                        plateType === PlateType.SPEAR_BIO ||
                        isEdit,
                    },
                  ]}
                  selectedValue={wellShape}
                  allowDisable
                  onChange={(value) => {
                    handleChangeWellShape(value);
                  }}
                />
                <img
                  className="image-well-preview well-surface-preview"
                  alt="Well Shape"
                  src={`./svg/plate-type-icon/${wellShapeImage}.svg`}
                />
              </div>
            </div>
          </div>
          <div className="plate-general-information__right-section">
            <p className="label-well-selection">
              Select Well Number
              <sup style={{color: 'red'}}>*</sup>
            </p>
            <div style={{margin: '8px 0px'}}>
              <ToggleRadioButton
                isCornerRounded={false}
                options={wellNumberOptions}
                selectedValue={wellLabel}
                onChange={(value, idx) => {
                  handleChangeWellNumber(value, idx);
                }}
              />
            </div>
            <img
              className="image-plate-preview"
              alt="Well Preview"
              src={`./svg/plate-type-icon/${wellNumberImage}.svg`}
            />
          </div>
        </span>
      </div>
      <div className="plate-view-info-desc__plate-description">
        <div>
          <div className="section-title bordered">Plate Description</div>
          <div className="section-additional-info italic">Specify the plate description below.</div>
        </div>
        <span className="plate-description__plate-field">
          <span className="plate-description__field-name-part-number">
            <PlateDescriptionField
              label="Plate Name"
              placeholder="Specify plate name..."
              isRequired
              value={plateName}
              onChange={handleChangeInputPlateName}
              onBlur={handleBlurInputPlateName}
              isError={isNameAlreadyTaken}
            />
            <PlateDescriptionField
              label="Plate Number"
              placeholder="N/A"
              value={partNumber}
              onChange={handleBlurInputPartNumber}
            />
          </span>
          <PlateDescriptionField
            label="Vendor"
            placeholder="Specify vendor name..."
            value={vendor}
            onChange={handleBlurInputVendor}
          />
          <PlateDescriptionField
            label="Notes"
            placeholder="Specify note for this plate..."
            value={notes}
            onChange={handleBlurInputNotes}
            mode="textarea"
          />
        </span>
      </div>
    </div>
  );
}

export default withIntl(PlateViewInfoDesc);

PlateViewInfoDesc.propTypes = {
  plateTypeEditorState: PropTypes.any.isRequired,
  onSetWellNumber: PropTypes.func.isRequired,
  onSetWellLabel: PropTypes.func.isRequired,
  onSetWellBottomProfile: PropTypes.func.isRequired,
  onSetWellShape: PropTypes.func.isRequired,
  onSetPlateName: PropTypes.func.isRequired,
  onSetPlateType: PropTypes.func.isRequired,
  onSetColPitchAssaySample: PropTypes.func.isRequired,
  onSetPartNumber: PropTypes.func.isRequired,
  onSetVendor: PropTypes.func.isRequired,
  onSetNotes: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
  isEdit: PropTypes.bool.isRequired,
  maxCharacters: PropTypes.number,
  labwareBusinessIsLabwareNameAlreadyExist: PropTypes.func.isRequired,
  onSetColumnPitch: PropTypes.func.isRequired,
  onSetRowPitch: PropTypes.func.isRequired,
};

PlateViewInfoDesc.defaultProps = {
  maxCharacters: 45,
};
